/** TanStack Query hooks for the Doc Kit API contract. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchBlob } from '../lib/api-client';
import type {
  CancelDocKitBatchInput,
  CancelDocKitBatchResponse,
  CancelDocumentInput,
  CreateDocKitBatchInput,
  CreateDocKitBatchResponse,
  CreateDocKitPlanInput,
  CreateDocProfileRevisionInput,
  CreateDocProfileRevisionResponse,
  DocKitBatchResponse,
  DocKitCatalogResponse,
  DocKitPlanResponse,
  DocKitProfileResponse,
  DocKitResponse,
  DocumentDetailResponse,
  DocumentEventsResponse,
  DocumentListFilter,
  DocumentListResponse,
  DocumentRow,
  PatchDocumentInput,
  PlatformDocTemplateResponse,
  PlatformDocTemplatesResponse,
  PublishDocKitBatchInput,
  PublishDocKitBatchResponse,
  PublishDocumentInput,
  PublishDocumentResponse,
  RefreshDocumentInput,
  ReopenDocumentInput,
  ReviewDocumentInput,
} from '@payroll/shared';

export const docKitQueryKeys = {
  catalog: () => ['hr', 'doc-kit', 'catalog'] as const,
  profile: () => ['hr', 'doc-kit', 'profile'] as const,
  plan: () => ['hr', 'doc-kit', 'plan'] as const,
  batches: () => ['hr', 'doc-kit', 'batch'] as const,
  batch: (id: string) => ['hr', 'doc-kit', 'batch', id] as const,
  documents: () => ['hr', 'documents'] as const,
  documentList: (filter: DocumentListFilter) => [
    'hr', 'documents', filter.status ?? '', filter.batch_id ?? '', filter.code ?? '',
  ] as const,
  document: (id: string) => ['hr', 'document', id] as const,
  documentPreview: (id: string) => ['hr', 'document', id, 'preview'] as const,
  documentPdf: (id: string) => ['hr', 'document', id, 'pdf'] as const,
  documentEvents: (id: string) => ['hr', 'document', id, 'events'] as const,
  policies: () => ['hr', 'policies'] as const,
  myPolicies: () => ['me', 'policies'] as const,
  platformTemplates: () => ['platform', 'doc-templates'] as const,
};

type DocumentMutationInput<T> = { id: string; body: T };
type DocumentIdInput = { id: string };
type PlatformTemplateVersionInput = { code: string; version: number };

function isUnavailable<T extends object>(response: DocKitResponse<T>): response is { unavailable: string } {
  return 'unavailable' in response;
}

function invalidateDocument(qc: ReturnType<typeof useQueryClient>, document: DocumentRow): void {
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.documents() });
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.batch(document.batch_id) });
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.document(document.id) });
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.documentPreview(document.id) });
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.documentPdf(document.id) });
  void qc.invalidateQueries({ queryKey: docKitQueryKeys.documentEvents(document.id) });
}

function documentPath(id: string, suffix = ''): string {
  return `/hr/documents/${encodeURIComponent(id)}${suffix}`;
}

export function useDocKitCatalog() {
  return useQuery({
    queryKey: docKitQueryKeys.catalog(),
    queryFn: () => apiFetch<DocKitResponse<DocKitCatalogResponse>>('/hr/doc-kit/catalog'),
    staleTime: 30_000,
  });
}

export function useDocKitProfile() {
  return useQuery({
    queryKey: docKitQueryKeys.profile(),
    queryFn: () => apiFetch<DocKitResponse<DocKitProfileResponse>>('/hr/doc-kit/profile'),
    staleTime: 15_000,
  });
}

export function useCreateDocProfileRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDocProfileRevisionInput) =>
      apiFetch<DocKitResponse<CreateDocProfileRevisionResponse>>('/hr/doc-kit/profile/revisions', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.profile() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.plan() });
    },
  });
}

export function useCreateDocKitPlan() {
  return useMutation({
    mutationFn: (body: CreateDocKitPlanInput) =>
      apiFetch<DocKitResponse<DocKitPlanResponse>>('/hr/doc-kit/plan', { method: 'POST', body }),
  });
}

export function useCreateDocKitBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDocKitBatchInput) =>
      apiFetch<DocKitResponse<CreateDocKitBatchResponse>>('/hr/doc-kit/batches', { method: 'POST', body }),
    onSuccess: (data) => {
      if (isUnavailable(data)) return;
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.profile() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.batches() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.documents() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.batch(data.batch.id) });
    },
  });
}

export function useDocKitBatch(id: string | undefined) {
  return useQuery({
    queryKey: docKitQueryKeys.batch(id ?? ''),
    queryFn: () => apiFetch<DocKitResponse<DocKitBatchResponse>>(`/hr/doc-kit/batches/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || isUnavailable(data)) return false;
      return data.documents.some((document) => document.job?.state === 'pending' || document.job?.state === 'running')
        ? 3_000
        : false;
    },
  });
}

export function usePublishDocKitBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<PublishDocKitBatchInput>) =>
      apiFetch<DocKitResponse<PublishDocKitBatchResponse>>(`/hr/doc-kit/batches/${encodeURIComponent(id)}/publish`, { method: 'POST', body }),
    onSuccess: (data, variables) => {
      if (isUnavailable(data)) return;
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.batch(variables.id) });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.documents() });
      data.published.forEach((id) => {
        void qc.invalidateQueries({ queryKey: docKitQueryKeys.document(id) });
        void qc.invalidateQueries({ queryKey: docKitQueryKeys.documentEvents(id) });
      });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.policies() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.myPolicies() });
    },
  });
}

export function useCancelDocKitBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<CancelDocKitBatchInput>) =>
      apiFetch<DocKitResponse<CancelDocKitBatchResponse>>(`/hr/doc-kit/batches/${encodeURIComponent(id)}/cancel`, { method: 'POST', body }),
    onSuccess: (data, variables) => {
      if (isUnavailable(data)) return;
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.batch(variables.id) });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.documents() });
      void qc.invalidateQueries({ queryKey: ['hr', 'document'] });
    },
  });
}

export function useDocuments(filter: DocumentListFilter = {}) {
  const search = new URLSearchParams();
  if (filter.status) search.set('status', filter.status);
  if (filter.batch_id) search.set('batch_id', filter.batch_id);
  if (filter.code) search.set('code', filter.code);
  const suffix = search.size ? `?${search.toString()}` : '';
  return useQuery({
    queryKey: docKitQueryKeys.documentList(filter),
    queryFn: () => apiFetch<DocKitResponse<DocumentListResponse>>(`/hr/documents${suffix}`),
    staleTime: 15_000,
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: docKitQueryKeys.document(id ?? ''),
    queryFn: () => apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id!)),
    enabled: Boolean(id),
  });
}

export function usePatchDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<PatchDocumentInput>) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id), { method: 'PATCH', body }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function useRefreshDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<RefreshDocumentInput>) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/refresh'), { method: 'POST', body }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function useReviewDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<ReviewDocumentInput>) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/review'), { method: 'POST', body }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function useReopenDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<ReopenDocumentInput>) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/reopen'), { method: 'POST', body }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function useRetryDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: DocumentIdInput) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/retry'), { method: 'POST' }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function usePublishDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<PublishDocumentInput>) =>
      apiFetch<DocKitResponse<PublishDocumentResponse>>(documentPath(id, '/publish'), { method: 'POST', body }),
    onSuccess: (data) => {
      if (isUnavailable(data)) return;
      invalidateDocument(qc, data.document);
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.policies() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.myPolicies() });
    },
  });
}

export function useCreateDocumentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: DocumentIdInput) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/new-version'), { method: 'POST' }),
    onSuccess: (data, variables) => {
      if (isUnavailable(data)) return;
      invalidateDocument(qc, data.document);
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.document(variables.id) });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.documentEvents(variables.id) });
    },
  });
}

export function useCancelDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: DocumentMutationInput<CancelDocumentInput>) =>
      apiFetch<DocKitResponse<DocumentDetailResponse>>(documentPath(id, '/cancel'), { method: 'POST', body }),
    onSuccess: (data) => {
      if (!isUnavailable(data)) invalidateDocument(qc, data.document);
    },
  });
}

export function useDocumentPreview(id: string | undefined) {
  return useQuery({
    queryKey: docKitQueryKeys.documentPreview(id ?? ''),
    queryFn: () => apiFetchBlob(documentPath(id!, '/preview')),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useDocumentPdf(id: string | undefined) {
  return useQuery({
    queryKey: docKitQueryKeys.documentPdf(id ?? ''),
    queryFn: () => apiFetchBlob(documentPath(id!, '/pdf')),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useDocumentEvents(id: string | undefined) {
  return useQuery({
    queryKey: docKitQueryKeys.documentEvents(id ?? ''),
    queryFn: () => apiFetch<DocKitResponse<DocumentEventsResponse>>(documentPath(id!, '/events')),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function usePlatformDocTemplates() {
  return useQuery({
    queryKey: docKitQueryKeys.platformTemplates(),
    queryFn: () => apiFetch<PlatformDocTemplatesResponse>('/platform/doc-templates'),
    staleTime: 30_000,
  });
}

function useChangePlatformDocTemplate(action: 'publish' | 'retire') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, version }: PlatformTemplateVersionInput) =>
      apiFetch<PlatformDocTemplateResponse>(
        `/platform/doc-templates/${encodeURIComponent(code)}/versions/${encodeURIComponent(String(version))}/${action}`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.platformTemplates() });
      void qc.invalidateQueries({ queryKey: docKitQueryKeys.catalog() });
    },
  });
}

export function usePublishPlatformDocTemplate() {
  return useChangePlatformDocTemplate('publish');
}

export function useRetirePlatformDocTemplate() {
  return useChangePlatformDocTemplate('retire');
}
